import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X, Send, Loader2, MessageCircle } from "lucide-react";
import { waLink } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HANDOFF_TOKEN = "[HANDOFF_WHATSAPP]";

const GREETING =
  "Ciao! Sono l'assistente virtuale XMOVE. Dimmi pure: cerchi un'auto, hai dubbi su deducibilità e IVA, o vuoi capire quanto spenderesti al mese?";

const QUICK = ["Quanto costa una BMW X1?", "Posso detrarre l'IVA?", "Serve per forza l'anticipo?"];

const getSid = () => {
  let sid = localStorage.getItem("xmove-chat-sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("xmove-chat-sid", sid);
  }
  return sid;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const location = useLocation();
  const listRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: GREETING }]);
    }
  }, [open]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const handoffUrl = () => {
    const userMsgs = messages
      .filter((m) => m.role === "user")
      .slice(-3)
      .map((m) => m.text)
      .join(" | ");
    const text = `Ciao Andrea, ti scrivo dalla chat del sito XMOVE. Stavamo parlando di: ${userMsgs}`.slice(0, 400);
    return waLink(text);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setHandoff(false);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setStreaming(true);
    let full = "";
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: getSid(), pagina: location.pathname }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        full += chunk;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
          return copy;
        });
      }
    } catch {
      full = "Connessione assente: scrivici su WhatsApp al +39 379 379 2323, rispondiamo subito.";
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: full };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
    if (full.includes(HANDOFF_TOKEN)) {
      const clean = full.replaceAll(HANDOFF_TOKEN, "").trim();
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: clean };
        return copy;
      });
      setHandoff(true);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-open-btn"
        aria-label="Apri assistente virtuale XMOVE"
        className="fixed bottom-[92px] right-4 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-xmove-orange text-white shadow-lift transition-all hover:bg-xmove-orangeDark hover:scale-105 active:scale-95 md:bottom-6 md:right-6"
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
        {!open && <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-xmove-green" />}
      </button>

      {open && (
        <div
          className="fixed bottom-[156px] right-4 z-[60] flex h-[min(600px,calc(100dvh-172px))] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-xmove-ink/10 bg-white shadow-lift md:bottom-24 md:right-6 md:h-[min(640px,calc(100dvh-128px))] md:max-w-md"
          data-testid="chat-panel"
        >
          <div className="flex items-center gap-3 bg-xmove-night px-5 py-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-xmove-orange">
              <Sparkles size={17} className="text-white" />
            </span>
            <span>
              <span className="block font-display text-sm font-bold text-white">XMOVE Assistant</span>
              <span className="block text-[11px] text-white/60">Consulente virtuale · risponde in tempo reale</span>
            </span>
          </div>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto bg-xmove-cream px-4 py-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-md bg-xmove-orange text-white"
                      : "rounded-bl-md border border-xmove-ink/8 bg-white text-xmove-ink shadow-soft"
                  }`}
                >
                  {m.text || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          {handoff && (
            <div className="border-t border-xmove-green/25 bg-xmove-green/10 px-4 py-2.5" data-testid="chat-handoff">
              <p className="text-[11px] font-semibold leading-snug text-xmove-ink">
                Ti passo Andrea Vecchioni, amministratore XMOVE — la conversazione arriva già scritta:
              </p>
              <a
                href={handoffUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chat-handoff-btn"
                className="mt-1.5 flex w-full items-center justify-center gap-2 rounded-xl bg-xmove-green py-2.5 text-sm font-bold text-white transition-all hover:brightness-95 active:scale-[0.98]"
              >
                <MessageCircle size={16} />
                Parla con Andrea su WhatsApp
              </a>
            </div>
          )}

          {!handoff && messages.filter((m) => m.role === "user").length >= 2 && (
            <div className="flex items-center gap-3 border-t border-xmove-ink/8 bg-white px-4 py-2" data-testid="chat-soft-cta">
              <p className="flex-1 text-[11px] leading-snug text-xmove-muted">
                Ti metto in contatto con il nostro responsabile commerciale,{" "}
                <b className="text-xmove-ink">rispondiamo in pochi minuti</b>.
              </p>
              <a
                href={handoffUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chat-soft-whatsapp-btn"
                className="flex shrink-0 items-center gap-1.5 rounded-full border-2 border-xmove-green px-3 py-1.5 text-[11px] font-bold text-xmove-green transition-colors hover:bg-xmove-green hover:text-white"
              >
                <MessageCircle size={13} />
                WhatsApp
              </a>
            </div>
          )}

          {messages.length <= 1 && !handoff && (
            <div className="flex flex-wrap gap-2 border-t border-xmove-ink/8 bg-white px-4 py-3">
              {QUICK.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  data-testid={`chat-quick-${q.slice(0, 12).replace(/\W/g, "-")}`}
                  className="rounded-full border border-xmove-orange/30 bg-xmove-orange/8 px-3 py-1.5 text-xs font-semibold text-xmove-orange transition-colors hover:bg-xmove-orange hover:text-white"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 border-t border-xmove-ink/8 bg-white p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Scrivi la tua domanda…"
              data-testid="chat-input"
              className="flex-1 rounded-xl border border-xmove-ink/15 bg-xmove-cream px-4 py-2.5 text-sm outline-none transition-all focus:border-xmove-orange focus:ring-2 focus:ring-xmove-orange/20"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="chat-send-btn"
              aria-label="Invia messaggio"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-xmove-orange text-white transition-all hover:bg-xmove-orangeDark disabled:opacity-50"
            >
              {streaming ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};
